"use client";
import { useEffect, useRef, useState } from "react";
import {
  setLocalMicOn,
  setLocalVideoOn,
  setSelectedMic,
  setSelectedCamera,
} from "@/store/slices/callSlice";

import {
  Card,
  CardHeader,
  CardContent,
  // CardFooter,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hook";
import home from "../assets/CNB1.jpeg";
import secure from "../assets/CNB2.jpeg";
// import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
// import LanguageChangePage from "@/components/common/LanguageChangePage";

interface UserLobbyPageProps {
  connect: (roomId: string, userName: string) => void;
  connectionStatus: string;
  isWaitingForAgent: boolean;
  InteractionData?: any;
  EstimedWaitTime?: number;
  // abortSignalRConnection: () => void;
}

// ---- Local persistence helpers ----
const PERSIST_KEY = "lobbyPrefs";

type LobbyPrefs = {
  localMicOn: boolean;
  localVideoOn: boolean;
  selectedMic?: string;
  selectedCamera?: string;
  roomId?: string;
  username?: string;
  shouldAutoConnect?: boolean;
};

const loadPrefs = (): LobbyPrefs | null => {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    return raw ? (JSON.parse(raw) as LobbyPrefs) : null;
  } catch {
    return null;
  }
};

const savePrefs = (patch: Partial<LobbyPrefs>) => {
  try {
    const current = loadPrefs() || {};
    const next = { ...current, ...patch } as LobbyPrefs;
    localStorage.setItem(PERSIST_KEY, JSON.stringify(next));
  } catch {
    // no-op
  }
};

export default function UserLobbyPage({
  connect,
  connectionStatus,
  isWaitingForAgent,
  InteractionData,
  EstimedWaitTime,
}: // abortSignalRConnection,
UserLobbyPageProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const dispatch = useAppDispatch();

  // Redux state
  const localMicOn = useAppSelector((s) => s.call.localMicOn);
  const localVideoOn = useAppSelector((s) => s.call.localVideoOn);
  const selectedMic = useAppSelector((s) => s.call.selectedMic);
  const selectedCamera = useAppSelector((s) => s.call.selectedCamera);

  // Local-only state for lobby
  const [lobbyStream, setLobbyStream] = useState<MediaStream | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isLoadingMedia, setIsLoadingMedia] = useState(true);
  const [microphoneVolume, setMicrophoneVolume] = useState(0);
  // Reconnect+meter refs
  const hasAttemptedConnectRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  // Load user data
  const UserData = (() => {
    try {
      const raw = localStorage.getItem("customerConnect");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const fallbackUsername = UserData
    ? `${UserData.values.first_name ?? ""} ${
        UserData.values.last_name ?? ""
      }`.trim() || "Guest"
    : "Guest";

  const defaultRoomId = "defaultRoom";

  // Restore persisted preferences on mount
  useEffect(() => {
    const prefs = loadPrefs();
    if (prefs) {
      if (typeof prefs.localMicOn === "boolean")
        dispatch(setLocalMicOn(prefs.localMicOn));
      if (typeof prefs.localVideoOn === "boolean")
        dispatch(setLocalVideoOn(prefs.localVideoOn));
      if (prefs.selectedMic) dispatch(setSelectedMic(prefs.selectedMic));
      if (prefs.selectedCamera)
        dispatch(setSelectedCamera(prefs.selectedCamera));
    }
  }, [dispatch]);

  // Persist mic/cam state changes
  useEffect(() => {
    savePrefs({ localMicOn, localVideoOn, selectedMic, selectedCamera });
  }, [localMicOn, localVideoOn, selectedMic, selectedCamera]);

  // Acquire lobby stream
  useEffect(() => {
    let stopped = false;
    const getMedia = async () => {
      setIsLoadingMedia(true);
      setMediaError(null);

      const prefs = loadPrefs();
      const preferredMic = selectedMic || prefs?.selectedMic;
      const preferredCam = selectedCamera || prefs?.selectedCamera;

      const buildConstraints = (
        useDevices: boolean
      ): MediaStreamConstraints => ({
        audio:
          useDevices && preferredMic
            ? { deviceId: { exact: preferredMic } as any }
            : true,
        video:
          useDevices && preferredCam
            ? { deviceId: { exact: preferredCam } as any }
            : true,
      });

      try {
        let mediaStream: MediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia(
            buildConstraints(true)
          );
        } catch {
          mediaStream = await navigator.mediaDevices.getUserMedia(
            buildConstraints(false)
          );
        }
        if (stopped) return;

        setLobbyStream(mediaStream);

        // Attach to video element
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          try {
            await videoRef.current.play();
          } catch {}
        }

        // Setup analyser for mic level
        if (mediaStream.getAudioTracks().length > 0) {
          const AudioCtx =
            (window as any).AudioContext || (window as any).webkitAudioContext;
          const audioContext: AudioContext = new AudioCtx();
          audioContextRef.current = audioContext;

          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyserRef.current = analyser;
          dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

          const source = audioContext.createMediaStreamSource(mediaStream);
          source.connect(analyser);

          const tick = () => {
            if (!analyserRef.current || !dataArrayRef.current) return;
            analyserRef.current.getByteFrequencyData(dataArrayRef.current);
            const sum = dataArrayRef.current.reduce((a, b) => a + b, 0);
            const avg = sum / dataArrayRef.current.length;
            setMicrophoneVolume(Math.min(100, Math.max(0, avg * 0.7)));
            animationFrameIdRef.current = requestAnimationFrame(tick);
          };
          animationFrameIdRef.current = requestAnimationFrame(tick);
        }
      } catch (error: any) {
        setMediaError(
          error?.name === "NotAllowedError"
            ? "Camera/Microphone access denied. Please grant permissions."
            : error?.message || "Could not access media devices."
        );
      } finally {
        setIsLoadingMedia(false);
      }
    };

    getMedia();

    return () => {
      stopped = true;
      if (audioContextRef.current) audioContextRef.current.close();
      if (animationFrameIdRef.current)
        cancelAnimationFrame(animationFrameIdRef.current);
      lobbyStream?.getTracks().forEach((track) => track.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-connect logic
  useEffect(() => {
    const prefs = loadPrefs();
    const roomId = prefs?.roomId || defaultRoomId;
    const username = (prefs?.username || fallbackUsername).trim() || "Guest";

    savePrefs({ roomId, username });

    if (
      !hasAttemptedConnectRef.current &&
      connectionStatus !== "connected" &&
      connectionStatus !== "connecting"
    ) {
      if (prefs?.shouldAutoConnect) {
        connect(roomId, username);
        hasAttemptedConnectRef.current = true;
      } else {
        connect(roomId, username);
        hasAttemptedConnectRef.current = true;
      }
    }

    if (
      connectionStatus === "connected" ||
      connectionStatus === "disconnected" ||
      connectionStatus === "failed"
    ) {
      hasAttemptedConnectRef.current = false;
    }
  }, [connect, connectionStatus, fallbackUsername]);

  // Reset mic/cam when disconnected
  useEffect(() => {
    if (connectionStatus === "connected") {
      savePrefs({ shouldAutoConnect: true });
    }
    if (connectionStatus === "disconnected" || connectionStatus === "failed") {
      dispatch(setLocalMicOn(false));
      dispatch(setLocalVideoOn(false));
      savePrefs({
        localMicOn: false,
        localVideoOn: false,
        shouldAutoConnect: false,
      });
    }
  }, [connectionStatus, dispatch]);

  // Toggle functions
  const toggleCamera = () => {
    const next = !localVideoOn;
    dispatch(setLocalVideoOn(next));
  };

  const toggleMic = () => {
    const next = !localMicOn;
    dispatch(setLocalMicOn(next));
  };
  useEffect(() => {
    if (videoRef.current && lobbyStream) {
      videoRef.current.srcObject = lobbyStream;
      videoRef.current
        .play()
        .catch((err) => console.warn("Video play failed:", err));
    }
  }, [lobbyStream]);

  return (
    <div className="md:flex items-center justify-center bg-blue-50 p-4 font-[Inter]">
      <div className="md:flex w-full max-w-7xl justify-center gap-6">
        <Card className="flex w-full max-w-lg flex-col items-center">
          <CardHeader className="flex flex-col w-full items-center justify-center">
            <div className="flex w-full justify-between items-center">
              <div className="flex items-center space-x-2">
                <Avatar className="h-6 w-6 bg-blue-400 pb-[1px]">
                  <AvatarImage
                    src={loadPrefs()?.username}
                    alt={loadPrefs()?.username}
                  />
                  <AvatarFallback>
                    {loadPrefs()?.username?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <CardTitle className="font-bold text-gray-800">
                  {loadPrefs()?.username || fallbackUsername || "Guest"}
                </CardTitle>
              </div>
              <CardDescription className="text-gray-600">
                {/* {UserData?.email || "guest@example.com"} */}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="w-full space-y-6">
            <Card className="p-2">
              <CardHeader className="p-0 justify-center ">
                <CardTitle className="text-xl font-bold text-blue-700">
                  You're in the lobby
                </CardTitle>
                <CardDescription className="text-gray-600">
                  <div className="flex-col text-center ">
                    <p className="font-bold">
                      Your Queue position: {InteractionData ?? "N/A"}
                    </p>
                    <p className="mt-2 font-bold">
                      Estimated wait time: {EstimedWaitTime ?? "N/A"} Min
                    </p>
                  </div>
                  {/* <div className="md:flex gap-5 items-center text-center mt-2">
                    <p className="lg:mb-0 mb-1">You can Change language :</p>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="bg-blue-500 text-white font-bold" >Switch Language</Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[425px] bg-blue-500 p-0 m-0 border-0 rounded-lg">
                        <DialogTitle className="text-lg font-bold p-4 border-b">
                          Select New Language
                        </DialogTitle>
                        <LanguageChangePage  abortSignalRConnection={abortSignalRConnection}   />
                      </DialogContent>
                    </Dialog>
                    </div> */}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 mt-0">
                <p className="text-center text-lg text-blue-700 font-semibold">
                  {isWaitingForAgent
                    ? "Connected, waiting for an agent to join..."
                    : "Please wait till we connect you to our video expert"}
                </p>
              </CardContent>
            </Card>

            {/* Video Preview */}
            {isLoadingMedia ? (
              <div className="flex h-48 w-full items-center justify-center rounded-lg bg-gray-200 text-gray-600 shadow-inner">
                <p>Loading camera and microphone...</p>
              </div>
            ) : mediaError ? (
              <div className="flex h-48 w-full flex-col items-center justify-center rounded-lg bg-red-100 text-red-700 shadow-inner p-4 text-center">
                <p className="font-semibold text-lg mb-2">
                  Media Access Error:
                </p>
                <p>{mediaError}</p>
                <p className="text-sm mt-2">
                  Check browser permissions and device connections.
                </p>
              </div>
            ) : lobbyStream && lobbyStream.getVideoTracks().length > 0 ? (
              <>
                <video
                  ref={videoRef}
                  className="h-48 w-full rounded-lg bg-black object-cover shadow-inner"
                  autoPlay
                  playsInline
                  muted
                  style={{ display: localVideoOn ? "block" : "none" }}
                />
                {!localVideoOn && (
                  <div className="flex h-48 w-full items-center justify-center rounded-lg bg-gray-200 text-gray-600 shadow-inner">
                    Camera is off.
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-48 w-full items-center justify-center rounded-lg bg-gray-200 text-gray-600 shadow-inner">
                No camera detected.
              </div>
            )}

            {/* Mic/Camera Controls */}
            <div className="flex justify-center items-center space-x-8">
              <div className="flex justify-center space-x-2">
                <Button
                  size="icon"
                  variant="outline"
                  className={`h-12 w-12 rounded-full ${
                    localVideoOn
                      ? "bg-gray-100 hover:bg-gray-200"
                      : "bg-red-500 text-white"
                  }`}
                  onClick={toggleCamera}
                  disabled={
                    !lobbyStream || lobbyStream.getVideoTracks().length === 0
                  }
                >
                  {localVideoOn ? (
                    <Video className="h-6 w-6" />
                  ) : (
                    <VideoOff className="h-6 w-6" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className={`h-12 w-12 rounded-full ${
                    localMicOn
                      ? "bg-gray-100 hover:bg-gray-200"
                      : "bg-red-500 text-white"
                  }`}
                  onClick={toggleMic}
                  disabled={
                    !lobbyStream || lobbyStream.getAudioTracks().length === 0
                  }
                >
                  {localMicOn ? (
                    <Mic className="h-6 w-6" />
                  ) : (
                    <MicOff className="h-6 w-6" />
                  )}
                </Button>
              </div>

              {/* Microphone Level Meter */}
              {localMicOn && (
                <div className="w-full">
                  {lobbyStream && lobbyStream.getAudioTracks().length > 0 && (
                    <div className="w-full flex flex-col items-center">
                      <p className="text-sm text-gray-600 mb-2">
                        Microphone Level:
                      </p>
                      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden relative">
                        <div
                          className="h-full bg-green-500 transition-all duration-75 ease-out rounded-full"
                          style={{ width: `${microphoneVolume}%` }}
                        ></div>
                        <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-800 font-bold">
                          {Math.round(microphoneVolume)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>

          {/* <CardFooter className="w-full pt-6">
            <div className="w-full py-3 text-lg font-semibold text-center text-gray-700">
              {connectionStatus === "connecting"
                ? "Connecting to our service..."
                : isWaitingForAgent
                ? "Connected, waiting for an agent to join..."
                : "Please wait to connect to a service representative."}
            </div>
          </CardFooter> */}
        </Card>

        {/* Right-side marketing/info panel */}
        <div className="w-full cnrCard max-w-sm overflow-hidden md:mt-0 mt-3">
          <h2 className="text-2xl font-bold md:text-start text-center text-gray-800 mb-4">
            While You Wait
          </h2>

          {/* Wrapper with overflow-hidden */}
          <div className="flex gap-5 relative overflow-hidden ">
            {/* Track — scroll left continuously */}
            <div className="flex animate-scroll-cards whitespace-nowrap">
              <div className="flex space-x-4">
                {/* Card 1 */}
                <Card className="min-w-[250px] p-0 overflow-hidden shadow-md rounded-xl">
                  <img
                    src={home}
                    alt="Home Loan Offer"
                    className="w-full h-auto"
                  />
                  <div className="p-4">
                    <CardTitle className="text-xl font-bold text-gray-800">
                      Foreign Currency
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-1 mr-10">
                      Grow your savings with multi currency Protection
                    </CardDescription>
                  </div>
                </Card>

                {/* Card 2 */}
                <Card className="min-w-[250px] p-0 overflow-hidden shadow-md rounded-xl">
                  <img
                    src={secure}
                    alt="Secure Banking"
                    className="w-full h-auto"
                  />
                  <div className="p-4">
                    <CardTitle className="text-xl font-bold text-gray-800">
                      Canara NRI Platina
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-1">
                      Privilege that elevate every mile you travel.
                    </CardDescription>
                  </div>
                </Card>
              </div>
            </div>
            <div className="flex animate-scroll-cards whitespace-nowrap">
              {/* Duplicate row for seamless looping */}
              <div className="flex space-x-4">
                {/* Card 1 */}
                <Card className="min-w-[250px] p-0 overflow-hidden shadow-md rounded-xl">
                  <img
                    src={home}
                    alt="Home Loan Offer"
                    className="w-full h-auto"
                  />
                  <div className="p-4">
                    <CardTitle className="text-xl font-bold text-gray-800">
                      Foreign Currency
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-1 mr-10">
                      Grow your savings with multi currency Protection
                    </CardDescription>
                  </div>
                </Card>

                {/* Card 2 */}
                <Card className="min-w-[250px] p-0 overflow-hidden shadow-md rounded-xl">
                  <img
                    src={secure}
                    alt="Secure Banking"
                    className="w-full h-auto"
                  />
                  <div className="p-4">
                    <CardTitle className="text-xl font-bold text-gray-800">
                      Canara NRI Platina
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-1">
                      Privilege that elevate every mile you travel.
                    </CardDescription>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
